import { useState } from "react";
import dayjs from "dayjs";
import { Icon } from "../../../../../../components";
import { Button } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../../../components/modal/Dialog";
import { useTwoMarkOpenBankingEvidenceSubmitted } from "~/hooks/api/v2/two/useTwoMarkOpenBankingEvidenceSubmitted";

const TWO_CREDIT_BUILDER_URL =
  "https://checkout.two.inc/credit_limit_change/credit_builder";

type Props = {
  accountDetailsLastSubmittedAt: Date;
  legalEntityId: number;
};

export const TwoOpenBankingRefreshBanner = ({
  accountDetailsLastSubmittedAt,
  legalEntityId,
}: Props) => {
  const isStale = dayjs(accountDetailsLastSubmittedAt).isBefore(
    dayjs().subtract(3, "months"),
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const { mutate: markSubmitted, isPending } =
    useTwoMarkOpenBankingEvidenceSubmitted();

  if (!isStale) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col justify-between gap-2 rounded-lg border border-orange-300 bg-orange-100 px-3 py-2 text-sm text-orange-800 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <Icon icon="ALERT" />
          <p>
            <span className="font-semibold">Recommended action</span> &ndash;{" "}
            <span>
              Refresh your open banking details to retain and potentially
              increase your credit limit.
            </span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => window.open(TWO_CREDIT_BUILDER_URL, "_blank")}
            className="cursor-pointer font-bold text-nowrap underline underline-offset-2"
          >
            Submit details
          </button>
          <span className="text-orange-400">|</span>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="cursor-pointer font-semibold text-nowrap"
          >
            Already submitted
          </button>
        </div>
      </div>

      <Dialog active={dialogOpen} onDismiss={() => setDialogOpen(false)}>
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setDialogOpen(false)}
          title="Confirm submission & snooze reminder"
          description="Snooze this reminder for 3 months if you have recently submitted open banking details. As Two, our financial partner, periodically review all Sellar Credit accounts, up to date banking information ensures credit limits remain accurate. You may also qualify for a higher limit."
        />
        <DialogContent>
          <div className="flex flex-col gap-4">
            <div className="flex justify-end gap-2">
              <Button
                color="secondary"
                onClick={() => setDialogOpen(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={isPending}
                onClick={() => {
                  markSubmitted(
                    { legalEntityId },
                    {
                      onSuccess: () => setDialogOpen(false),
                    },
                  );
                }}
              >
                {isPending ? "Confirming…" : "Confirm & Snooze"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
